import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getCsrfToken } from '@/utils/csrf';

const GAMES_TO_LOAD_PER_CLICK = 9;

interface AppContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showAllAdultContent: boolean;
  toggleAllAdultContent: () => void;
  // Új állapotok a főoldalhoz
  indexLoadedCount: number;
  setIndexLoadedCount: (count: number) => void;
  indexSelectedGenre: string;
  setIndexSelectedGenre: (genre: string) => void;
  indexSelectedLetter: string | null;
  setIndexSelectedLetter: (letter: string | null) => void;
  resetIndexState: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser, refreshCurrentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllAdultContent, setShowAllAdultContent] = useState(false);

  // A főoldal állapota
  const [indexLoadedCount, setIndexLoadedCount] = useState(GAMES_TO_LOAD_PER_CLICK);
  const [indexSelectedGenre, setIndexSelectedGenre] = useState<string>("all");
  const [indexSelectedLetter, setIndexSelectedLetter] = useState<string | null>(null);

  // Szinkronizálja a felnőtt tartalom állapotát a bejelentkezett felhasználó beállításaival
  useEffect(() => {
    const userPreference = currentUser?.showAdultContent ?? false;
    if (showAllAdultContent !== userPreference) {
      setShowAllAdultContent(userPreference);
    }
  }, [currentUser]);

  const toggleAllAdultContent = useCallback(async () => {
    const newValue = !showAllAdultContent;
    setShowAllAdultContent(newValue); // Optimista UI frissítés

    if (currentUser) {
      try {
        // Az új, dedikált végpont használata, ami nem kér jelszót
        const response = await fetch('/api/toggle_adult_content', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
        });

        if (!response.ok) {
          setShowAllAdultContent(!newValue); // Hiba esetén visszaállítás
          console.error('Nem sikerült a beállítás mentése.');
        } else {
          refreshCurrentUser?.(); // Frissítjük a currentUser-t a konzisztencia érdekében
        }
      } catch (error) {
        setShowAllAdultContent(!newValue); // Hiba esetén visszaállítás
      }
    }
  }, [showAllAdultContent, currentUser, refreshCurrentUser]);

  const resetIndexState = () => {
    setIndexLoadedCount(GAMES_TO_LOAD_PER_CLICK);
    setIndexSelectedGenre("all");
    setIndexSelectedLetter(null);
    setSearchQuery("");
  };

  const value = useMemo(() => ({
    searchQuery,
    setSearchQuery,
    showAllAdultContent,
    toggleAllAdultContent,
    indexLoadedCount,
    setIndexLoadedCount,
    indexSelectedGenre,
    setIndexSelectedGenre,
    indexSelectedLetter,
    setIndexSelectedLetter,
    resetIndexState
  }), [searchQuery, showAllAdultContent, toggleAllAdultContent, indexLoadedCount, indexSelectedGenre, indexSelectedLetter]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};